import { Cell } from '@table-library/react-table-library/table';
import numeral from 'numeral';

import { comparisonColumns, contributorColumns } from '../GscTools/columns';
import { MATCH_POLICY, SEGMENT_COLORS, escapeRegex, segmentLabel } from './brand';

/**
 * The columns this report defines on top of the shared GSC tool sets.
 *
 * Three of them exist because this report's rows are not keyed by something Search Console
 * returned: a segment row is the user's own rule seen from one side, a definition row is the rule
 * itself, and an evidence row carries a reason that only makes sense beside the rule that
 * produced it.
 */

/** A segment's name, with the dot that ties it to its line in the chart below. */
function segmentColumn(t) {
    return {
        label: t('tools.brand_non_brand.comparison.segment'),
        key: 'key',
        data: 'key',
        sortType: 'string',
        cellSize: 'minmax(180px, 1.6fr)',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            const label = segmentLabel(t, data);
            if (csvDownload) return label;

            return (
                <span className="brand-segment-name">
                    <i
                        className="brand-segment-dot"
                        style={{ background: SEGMENT_COLORS[data] }}
                        aria-hidden="true"
                    />
                    {label}
                </span>
            );
        },
    };
}

/**
 * The share column, named after its denominator wherever it appears.
 *
 * It is a share of the clicks the rule could classify, never of the property's traffic, so the
 * heading says "known-query" and the empty cell says which of the two reasons it is empty for.
 * Rounding happens here and nowhere earlier: the figure is divided from raw click counts.
 */
function shareColumn(t) {
    return {
        label: t('tools.brand_non_brand.comparison.share'),
        key: 'share',
        data: 'share',
        sortType: 'number',
        cellSize: 'minmax(160px, 1.4fr)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (item.share === null || item.share === undefined) {
                const label = item.shareReason
                    ? t(`tools.brand_non_brand.comparison.share_unavailable.${item.shareReason}`)
                    : t('tools.gsc.not_returned');
                return csvDownload ? label : <span className="text-muted">{label}</span>;
            }

            const formatted = numeral(item.share).format('0.0%');
            return csvDownload ? formatted : <strong>{formatted}</strong>;
        },
    };
}

/**
 * Block 02 — the two sides of the rule, this window against its baseline, plus each side's share
 * of the clicks that could be classified at all.
 *
 * The shared comparison set does the metrics, so a brand row and a page row elsewhere in the app
 * report the same numbers under the same headings; only the key column and the share are this
 * report's own.
 */
export function comparisonTableColumns(t, { hasPosition }) {
    const shared = comparisonColumns(t, {
        hasPosition,
        keyLabel: t('tools.brand_non_brand.comparison.segment'),
        isUrl: false,
    });

    return [segmentColumn(t), ...shared.slice(1), shareColumn(t)];
}

/**
 * Block 01 — the definition as it was compiled.
 *
 * Each term is shown both as it was typed and as the alternative it became, because those differ
 * precisely where it matters: a term carrying a dot, a plus or a bracket is escaped, and seeing
 * the escape is how a user knows the character was matched literally rather than as a pattern.
 */
export function definitionColumns(t) {
    return [
        {
            label: t('tools.brand_non_brand.definition.term'),
            key: 'match',
            data: 'match',
            sortType: 'string',
            cellSize: 'minmax(200px, 2fr)',
            Component: Cell,
        },
        {
            label: t('tools.brand_non_brand.definition.name'),
            key: 'name',
            data: 'name',
            sortType: 'string',
            cellSize: 'minmax(160px, 1.4fr)',
            Component: Cell,
            renderCell: ({ data }) => data || '—',
        },
        {
            label: t('tools.brand_non_brand.definition.mode'),
            key: 'mode',
            data: 'mode',
            sortType: 'string',
            cellSize: 'minmax(220px, 2fr)',
            Component: Cell,
        },
        {
            label: t('tools.brand_non_brand.definition.compiled'),
            key: 'compiled',
            data: 'compiled',
            sortType: 'string',
            cellSize: 'minmax(220px, 2fr)',
            Component: Cell,
            renderCell: ({ data, csvDownload }) =>
                csvDownload ? data : <code className="gsc-tool-expression">{data}</code>,
        },
    ];
}

/** The definition's rows: one per term, or the single expression an advanced rule is. */
export function definitionRows(t, { policy, terms, pattern }) {
    if (policy === MATCH_POLICY.REGEX) {
        return [
            {
                id: 'pattern',
                match: pattern,
                name: '',
                mode: t('tools.brand_non_brand.definition.modes.regex'),
                compiled: pattern,
            },
        ];
    }

    return (terms ?? []).map((term, index) => ({
        id: `${term.match}-${index}`,
        match: term.match,
        name: term.name,
        mode: t('tools.brand_non_brand.definition.modes.literal'),
        compiled: escapeRegex(term.match.trim()),
    }));
}

/**
 * Block 04 — the queries one side of the rule returned, and why each is on that side.
 *
 * The classification is a column rather than only a heading because the export is a file that
 * outlives the page it came from: a row that does not carry its own side is a click count nobody
 * can place. The reason is the last column, where a long sentence costs the metrics no width.
 */
export function evidenceColumns(t, { hasPosition, reasonFor }) {
    const shared = contributorColumns(t, {
        hasPosition,
        keyLabel: t('tools.gsc.columns.query'),
        isUrl: false,
    });

    return [
        shared[0],
        {
            label: t('tools.brand_non_brand.evidence.classification'),
            key: 'segment',
            data: 'segment',
            sortType: 'string',
            cellSize: 'minmax(130px, 1fr)',
            Component: Cell,
            renderCell: ({ data }) => segmentLabel(t, data),
        },
        ...shared.slice(1),
        {
            label: t('tools.brand_non_brand.evidence.reason'),
            key: 'reason',
            data: 'key',
            sortType: (array) => array,
            cellSize: 'minmax(260px, 2.6fr)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                const reason = reasonFor(item);
                if (csvDownload) return reason.text;

                // A row whose text disagrees with the side Search Console put it on is the one
                // the user most needs to see; it is marked, not hidden and not corrected.
                return reason.ambiguous ? (
                    <span className="brand-reason-flag">
                        <i className="icon-info" aria-hidden="true" />
                        {reason.text}
                    </span>
                ) : (
                    reason.text
                );
            },
        },
    ];
}

/** Block 05's item/value table. */
export const SCOPE_COLUMNS = (t) => [
    {
        label: t('tools.brand_non_brand.coverage.item'),
        key: 'item',
        data: 'item',
        sortType: 'string',
        cellSize: 'minmax(260px, 2fr)',
        Component: Cell,
    },
    {
        label: t('tools.brand_non_brand.coverage.value'),
        key: 'value',
        data: 'value',
        sortType: 'string',
        cellSize: 'minmax(260px, 2.4fr)',
        Component: Cell,
        renderCell: ({ data }) => data || '—',
    },
];
